import { getCartLevelDiscounts, getLineLevelDiscounts } from '@shared/lib/cart/server-cart';
import {
  DIAMOND_COLOR_SURCHARGE_PRODUCT_HANDLE,
  DIAMOND_CUT_SURCHARGE_PRODUCT_HANDLE,
  DIAMOND_PRODUCT_HANDLE,
  KIT_PRODUCT_HANDLE,
  KIT_VARIANT_OPTION,
  PREMIUM_PROCESSING_FEE_PRODUCT_HANDLE,
} from '@shared/lib/constants';
import { updateCart } from '@shared/lib/shopify';
import { CartItem, Money } from '@shared/lib/shopify/types';
import { Cart } from '@shared/lib/shopify/types/shopify-native';

export function getKitOption(cart: Cart) {
  const kitLine = getLineByHandle(cart, KIT_PRODUCT_HANDLE);
  if (!kitLine) return null;
  return kitLine.merchandise.selectedOptions.find((option) => option.name === KIT_VARIANT_OPTION)
    ?.value;
}

export function getLineByHandle(cart: Cart, handle: string) {
  return cart.lines.find((line) => line.merchandise.product.handle === handle);
}

export function getLinesByHandle(cart: Cart, handle: string) {
  return cart.lines.filter((line) => line.merchandise.product.handle === handle);
}

export function getLinesByProductId(cart: Cart, productId: string) {
  return cart.lines.filter((line) => line.merchandise.product.id === productId);
}

export function getLinesByVariantId(cart: Cart, variantId: string) {
  return cart.lines.filter((line) => line.merchandise.id === variantId);
}

export function getLineById(cart: Cart, lineId: string) {
  return cart.lines.find((line) => line.id === lineId);
}

export function getDiamondsTotalDollars(cart: Cart) {
  const diamondLines = (cart && getLinesByHandle(cart, DIAMOND_PRODUCT_HANDLE)) || [];
  return diamondLines.reduce((acc, line) => acc + parseInt(line.cost.totalAmount.amount), 0);
}

export function getSelectedDiamondSize(cart: Cart) {
  const diamondLine = getLineByHandle(cart, DIAMOND_PRODUCT_HANDLE);
  if (!diamondLine) return null;
  return diamondLine.merchandise.selectedOptions.find((option) => option.name === 'Size')?.value;
}

export function hasDiamond(cart: Cart) {
  return !!getSelectedDiamondSize(cart);
}

export function hasKit(cart: Cart) {
  return !!getLineByHandle(cart, KIT_PRODUCT_HANDLE);
}

export function hasShapeSurcharge(cart: Cart) {
  return !!getLineByHandle(cart, DIAMOND_CUT_SURCHARGE_PRODUCT_HANDLE);
}

export function hasColorSurcharge(cart: Cart) {
  return !!getLineByHandle(cart, DIAMOND_COLOR_SURCHARGE_PRODUCT_HANDLE);
}

export function diamondFullyCustomized(cart: Cart) {
  return getSelectedDiamondSize(cart) && hasShapeSurcharge(cart) && hasColorSurcharge(cart);
}

// In our business use case, we have some special constraint on the cart, e.g. if the diamond price is adjusted, the cut surcharge should be adjusted accordingly. The kit charge should not be waived if there is no diamond in the cart.

// async function rebalanceCart(cart: Cart) {
//   rebalanceSurcharges(cart);
//   rebalanceKitWaive(cart);
// }

export async function rebalanceSurcharges(cart: Cart) {
  // The diamond price is adjusted, the cut surcharge should be adjusted accordingly.
  // The kit charge should not be waived if there is no diamond in the cart.
  const diamondTotalDollars = getDiamondsTotalDollars(cart);
  const shapeLine = getLineByHandle(cart, DIAMOND_CUT_SURCHARGE_PRODUCT_HANDLE);
  const colorLine = getLineByHandle(cart, DIAMOND_COLOR_SURCHARGE_PRODUCT_HANDLE);

  const linesToUpdate = [];
  if (shapeLine && shapeLine.quantity !== diamondTotalDollars) {
    linesToUpdate.push({
      id: shapeLine.id,
      merchandiseId: shapeLine.merchandise.id,
      quantity: diamondTotalDollars,
    });
  }
  if (colorLine && colorLine.quantity !== diamondTotalDollars) {
    linesToUpdate.push({
      id: colorLine.id,
      merchandiseId: colorLine.merchandise.id,
      quantity: diamondTotalDollars,
    });
  }
  if (linesToUpdate.length) {
    await updateCart(cart.id, linesToUpdate);
  }
}

interface CartItems {
  kit: CartItem | undefined;
  premiumProcessingFee: CartItem | undefined;
  diamond: CartItem | undefined;
  cutSurcharge: CartItem | undefined;
}

export function getItemsFromCart(cart: Cart | undefined | null): CartItems {
  if (!cart)
    return {
      kit: undefined,
      premiumProcessingFee: undefined,
      diamond: undefined,
      cutSurcharge: undefined,
    };
  return {
    kit: cart?.lines.find((item) => item.merchandise.product.handle === KIT_PRODUCT_HANDLE),
    premiumProcessingFee: cart?.lines.find(
      (item) => item.merchandise.product.handle === PREMIUM_PROCESSING_FEE_PRODUCT_HANDLE
    ),
    diamond: cart?.lines.find((item) => item.merchandise.product.handle === DIAMOND_PRODUCT_HANDLE),
    cutSurcharge: cart?.lines.find(
      (item) => item.merchandise.product.handle === DIAMOND_CUT_SURCHARGE_PRODUCT_HANDLE
    ),
  };
}

interface CartDiscounts {
  kitDiscount: Money | undefined;
  diamondDiscount: Money | undefined;
  installmentDiscount: Money | undefined;
}

export function getDiscountsFromCart(cart: Cart | undefined | null): CartDiscounts {
  if (!cart)
    return {
      kitDiscount: undefined,
      diamondDiscount: undefined,
      installmentDiscount: undefined,
    };
  return {
    kitDiscount: getCartLevelDiscounts(cart).find((discount) =>
      discount.title.toLowerCase().includes('hair collection kit')
    )?.totalAmount,
    diamondDiscount: getLineLevelDiscounts(cart).find((discount) =>
      discount.title.toLowerCase().includes('bonded diamond')
    )?.totalAmount,
    installmentDiscount: getCartLevelDiscounts(cart).find((discount) =>
      discount.title.toLowerCase().includes('installment')
    )?.totalAmount,
  };
}
